// Enhanced Reporting.js with timezone detection and handling
import React, { useState, useEffect } from 'react';
import './Reporting.css';
import Header from '../../components/header/header';
// Hooks
import { useReportingDashboard, useReportForm } from '../../components/Reporting/hooks/useReportingDashboard';
// Services
import { reportingAPI } from '../../components/Reporting/APIServiceLayer/APIServiceLayer';
// Utils
import { 
  formatSchedule, 
  safeJsonParse, 
  getRecipientsArray, 
  getDateRangeStart, 
  getDateRangeEnd,
  getDefaultReportForm 
} from '../../components/Reporting/utils/reportingUtils';

// StackedToast System
import { ToastProvider, useToast } from '../../components/StackedToast/StackedToast';

// Components
import { 
  DashboardHeader, 
  StatsGrid, 
  ScheduledReportsSection, 
  RecentActivitySection, 
  ErrorBanner, 
  LoadingContainer
} from '../../components/Reporting/dashboard/DashboardComponents';

import { 
  CreateReportModal, 
  EditReportModal, 
  DeleteConfirmModal 
} from '../../components/Reporting/modals/ReportModals';

/**
 * Detect user's timezone
 */
const getUserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    console.warn('Could not detect timezone, defaulting to UTC:', error);
    return 'UTC';
  }
};

/**
 * Get timezone display name
 */
const getTimezoneDisplayName = (timezone) => {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en', {
      timeZone: timezone,
      timeZoneName: 'short'
    });
    const parts = formatter.formatToParts(now);
    const timeZoneName = parts.find(part => part.type === 'timeZoneName')?.value;
    return `${timezone} (${timeZoneName})`;
  } catch (error) {
    return timezone;
  }
};

/**
 * Get recommended period for a given frequency (for UI hints)
 */
const getRecommendedPeriodForFrequency = (frequency) => {
    const recommendations = {
        'daily': 'yesterday',
        'weekly': 'last_7_days',
        'monthly': 'last_month',
        'quarterly': 'last_quarter'
    };
    return recommendations[frequency] || 'last_30_days';
};

/**
 * Check if the selected period is optimal for the frequency
 */
const isPeriodOptimalForFrequency = (period, frequency) => {
    if (period === 'auto') return true;
    const recommended = getRecommendedPeriodForFrequency(frequency);
    return period === recommended;
};

/**
 * Create default report form with timezone detection
 */
const createDefaultReportForm = () => {
    const userTimezone = getUserTimezone();
    
    return {
        name: '',
        description: '',
        report_type: 'performance',
        frequency: 'weekly',
        frequency_config: { day_of_week: 1, time: '09:00' },
        timezone: userTimezone, // Auto-detect user's timezone
        date_range_type: 'rolling',
        date_range_config: { 
            period: 'auto',  // Default to auto, but user can change
            timezone: userTimezone
        },
        data_filters: {},
        report_sections: ['executive_summary', 'performance_metrics', 'trend_analysis'],
        format_settings: { format: 'pdf', style: 'executive' },
        recipients: [''],
        delivery_settings: { subject: 'Scheduled Report', body_template: 'default' }
    };
};

const ReportingDashboard = () => {
  const [userTimezone, setUserTimezone] = useState('UTC');
  
  // Loading states for downloads
  const [downloadingReports, setDownloadingReports] = useState([]);
  const [downloadingActivity, setDownloadingActivity] = useState([]);
  
  // StackedToast hook
  const { addToast } = useToast();

  // Detect user timezone on component mount
  useEffect(() => {
    const detectedTimezone = getUserTimezone();
    setUserTimezone(detectedTimezone);
    console.log('Detected user timezone:', detectedTimezone);
  }, []);

  const {
    loading,
    error,
    dashboardStats,
    scheduledReports,
    recentActivity,
    reportForm,
    showCreateModal,
    showEditModal,
    showDeleteConfirm,
    selectedReport,
    lastRefresh,
    setError,
    setReportForm,
    setShowCreateModal,
    setShowEditModal,
    setShowDeleteConfirm,
    setSelectedReport,
    setScheduledReports,
    setRecentActivity,
    loadDashboardData
  } = useReportingDashboard();

  // Form management for create modal
  const createForm = useReportForm(createDefaultReportForm(), () => {
    setShowCreateModal(false);
    loadDashboardData();
  });

  // Form management for edit modal
  const editForm = useReportForm(reportForm, () => {
    setShowEditModal(false);
    setSelectedReport(null);
    loadDashboardData();
  });

  // Report actions
  const handleToggleReport = async (reportId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'paused' : 'active';
      await reportingAPI.toggleScheduledReport(reportId, newStatus);
      
      setScheduledReports(reports => 
        reports.map(report => 
          report.id === reportId 
            ? { ...report, status: newStatus }
            : report
        )
      );
      
      addToast(
        `Report ${newStatus === 'active' ? 'activated' : 'paused'} successfully`,
        'success'
      );
    } catch (err) {
      console.error('Error toggling report:', err);
      setError('Failed to update report status');
      addToast('Failed to update report status', 'error');
    }
  };

  const handleEditReport = (report) => {
    setSelectedReport(report);
    
    const processedRecipients = getRecipientsArray(report.recipients);
    
    // Parse the existing timezone or fall back to user's current timezone
    const reportTimezone = report.timezone || userTimezone;
    
    const formData = {
      name: report.name || '',
      description: report.description || '',
      report_type: report.report_type || 'performance',
      frequency: report.frequency || 'weekly',
      frequency_config: safeJsonParse(report.frequency_config, { day_of_week: 1, time: '09:00' }),
      timezone: reportTimezone,
      date_range_type: report.date_range_type || 'rolling',
      // Parse existing date range config, ensure timezone is included
      date_range_config: {
        ...safeJsonParse(report.date_range_config, { period: 'auto' }),
        timezone: reportTimezone
      },
      data_filters: safeJsonParse(report.data_filters, {}),
      report_sections: safeJsonParse(report.report_sections, ['executive_summary', 'performance_metrics', 'trend_analysis']),
      format_settings: safeJsonParse(report.format_settings, { format: 'pdf', style: 'executive' }),
      recipients: processedRecipients.length > 0 ? processedRecipients : [''],
      delivery_settings: safeJsonParse(report.delivery_settings, { subject: 'Scheduled Report', body_template: 'default' })
    };
    
    editForm.setForm(formData);
    setShowEditModal(true);
  };

  const handleDeleteReport = (report) => {
    setSelectedReport(report);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteReport = async () => {
    try {
      await reportingAPI.deleteScheduledReport(selectedReport.id);
      setScheduledReports(reports => reports.filter(report => report.id !== selectedReport.id));
      setShowDeleteConfirm(false);
      setSelectedReport(null);
      addToast('Report deleted successfully', 'success');
    } catch (err) {
      console.error('Error deleting report:', err);
      setError('Failed to delete report');
      addToast('Failed to delete report', 'error');
    }
  };

  // Handle timezone change
  const handleTimezoneChange = (newTimezone, formType = 'create') => {
    const targetForm = formType === 'create' ? createForm : editForm;
    
    targetForm.updateForm({ 
      timezone: newTimezone,
      date_range_config: {
        ...targetForm.form.date_range_config,
        timezone: newTimezone
      }
    });

    addToast(`Timezone updated to ${getTimezoneDisplayName(newTimezone)}`, 'info', 3000);
  };

  // Handle period change independently from frequency
  const handlePeriodChange = (newPeriod, formType = 'create') => {
    const targetForm = formType === 'create' ? createForm : editForm;
    
    targetForm.updateForm({ 
      date_range_config: { 
        ...targetForm.form.date_range_config,
        period: newPeriod 
      }
    });

    // Show helpful message about the selection
    if (newPeriod === 'auto') {
      const recommendedPeriod = getRecommendedPeriodForFrequency(targetForm.form.frequency);
      addToast(`Will automatically use best period for ${targetForm.form.frequency} reports`, 'info', 3000);
    } else {
      // Check if the selection is optimal for the current frequency
      if (!isPeriodOptimalForFrequency(newPeriod, targetForm.form.frequency)) {
        const recommendedPeriod = getRecommendedPeriodForFrequency(targetForm.form.frequency);
        addToast(`Consider using recommended period for better alignment with ${targetForm.form.frequency} frequency`, 'warning', 5000);
      }
    }
  };

  // Handle frequency change - now independent of period
  const handleFrequencyChange = (newFrequency, formType = 'create') => {
    const targetForm = formType === 'create' ? createForm : editForm;
    
    // Update frequency
    targetForm.updateForm({ frequency: newFrequency });
    
    // Only suggest period change if current period is 'auto'
    const currentPeriod = targetForm.form.date_range_config?.period;
    if (currentPeriod === 'auto') {
      addToast(`Auto-period will select the best range for ${newFrequency} reports`, 'info', 3000);
    } else if (!isPeriodOptimalForFrequency(currentPeriod, newFrequency)) {
      // Show warning if current period isn't optimal for new frequency
      addToast(`Your current period may not be optimal for ${newFrequency} reports. Consider using Auto or adjusting manually.`, 'warning', 5000);
    }
  };

  const handleDownloadReport = async (report, isActivityItem = false) => {
    const reportId = report.id;
    const reportName = report.name || 'Report';

    try {
      // Add to loading state
      if (isActivityItem) {
        setDownloadingActivity(prev => [...prev, reportId]);
      } else {
        setDownloadingReports(prev => [...prev, reportId]);
      }

      // Show processing notification
      addToast(
        `Generating ${reportName}...`,
        'info',
        0 // Don't auto-dismiss
      );

      setError(null);

      // Determine file format from report settings
      console.log('=== FRONTEND DEBUG ===');
      console.log('Report object:', report);
      console.log('Format settings raw:', report.format_settings);
      console.log('Format settings type:', typeof report.format_settings);

      // Parse format settings - it might be a string or already an object
      let formatSettings;
      if (typeof report.format_settings === 'string') {
        formatSettings = safeJsonParse(report.format_settings, { format: 'pdf' });
      } else if (typeof report.format_settings === 'object' && report.format_settings !== null) {
        formatSettings = report.format_settings;
      } else {
        formatSettings = { format: 'pdf' };
      }

      console.log('Format settings parsed:', formatSettings);

      const fileFormat = formatSettings.format || 'pdf';
      console.log('File format determined:', fileFormat);

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').replace('T', '_').split('.')[0];
      const desiredFilename = `fdbk-report-${timestamp}.${fileFormat}`;
      console.log('Desired filename:', desiredFilename);
      
      // Get business_id for report generation
      const businessId = localStorage.getItem('selectedBusinessId');
      if (!businessId) {
        throw new Error('No business selected. Please select a business first.');
      }
      
      // Use the configured date range period for download
      // The backend will handle the actual date calculation based on the period and timezone
      const dateRangeConfig = safeJsonParse(report.date_range_config, { period: 'auto' });
      const period = dateRangeConfig.period;
      const reportTimezone = report.timezone || userTimezone;
      
      // For downloads, we'll let the backend handle date calculation based on timezone
      // But we need to provide start/end dates for the API
      const today = new Date();
      let startDate, endDate;
      
      if (period === 'auto') {
        // Use frequency-based logic for auto period
        if (report.frequency === 'daily') {
          startDate = new Date(today);
          startDate.setDate(today.getDate() - 1);
          endDate = new Date(today);
          endDate.setDate(today.getDate() - 1);
        } else if (report.frequency === 'weekly') {
          startDate = new Date(today);
          startDate.setDate(today.getDate() - 7);
          endDate = new Date(today);
          endDate.setDate(today.getDate() - 1);
        } else if (report.frequency === 'monthly') {
          // Calculate based on yesterday's date
          const yesterday = new Date(today);
          yesterday.setDate(today.getDate() - 1);
          startDate = new Date(yesterday.getFullYear(), yesterday.getMonth(), 1);
          endDate = yesterday;
        } else if (report.frequency === 'quarterly') {
          const lastQuarter = Math.floor(today.getMonth() / 3) - 1;
          const lastQuarterYear = lastQuarter < 0 ? today.getFullYear() - 1 : today.getFullYear();
          const adjustedQuarter = lastQuarter < 0 ? 3 : lastQuarter;
          startDate = new Date(lastQuarterYear, adjustedQuarter * 3, 1);
          endDate = new Date(lastQuarterYear, (adjustedQuarter + 1) * 3, 0);
        } else {
          // Default fallback
          startDate = new Date(today);
          startDate.setDate(today.getDate() - 30);
          endDate = new Date(today);
          endDate.setDate(today.getDate() - 1);
        }
      } else {
        // Use the specific period selected
        switch (period) {
          case 'yesterday':
            startDate = new Date(today);
            startDate.setDate(today.getDate() - 1);
            endDate = new Date(today);
            endDate.setDate(today.getDate() - 1);
            break;
          case 'last_7_days':
            startDate = new Date(today);
            startDate.setDate(today.getDate() - 7);
            endDate = new Date(today);
            endDate.setDate(today.getDate() - 1);
            break;
          case 'last_30_days':
            startDate = new Date(today);
            startDate.setDate(today.getDate() - 30);
            endDate = new Date(today);
            endDate.setDate(today.getDate() - 1);
            break;
          case 'last_month':
            startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            endDate = new Date(today.getFullYear(), today.getMonth(), 0);
            break;
          case 'this_month':
            startDate = new Date(today.getFullYear(), today.getMonth(), 1);
            endDate = today;
            break;
          case 'last_quarter':
            const lastQuarter = Math.floor(today.getMonth() / 3) - 1;
            const lastQuarterYear = lastQuarter < 0 ? today.getFullYear() - 1 : today.getFullYear();
            const adjustedQuarter = lastQuarter < 0 ? 3 : lastQuarter;
            startDate = new Date(lastQuarterYear, adjustedQuarter * 3, 1);
            endDate = new Date(lastQuarterYear, (adjustedQuarter + 1) * 3, 0);
            break;
          default:
            // Fallback to configured date range or last 30 days
            startDate = getDateRangeStart(report.date_range_config || {}) || new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
            endDate = getDateRangeEnd(report.date_range_config || {}) || today;
        }
      }
      
      // Format dates as YYYY-MM-DD
      const formatDate = (date) => {
        return date.toISOString().split('T')[0];
      };
      
      const startDateStr = formatDate(startDate);
      const endDateStr = formatDate(endDate);
      console.log(`Generating report for business ${businessId} from ${startDateStr} to ${endDateStr} (period: ${period}, timezone: ${reportTimezone})`);
      
      // Generate the report
      const generateResponse = await reportingAPI.generateReport({
        businessId: businessId,
        startDate: startDateStr,
        endDate: endDateStr,
        reportType: report.report_type || 'custom',
        customFilename: desiredFilename,
        timezone: reportTimezone, // Include timezone in request
        format: fileFormat // Include format (pdf or csv)
      });
      
      if (!generateResponse.success) {
        throw new Error('Failed to generate report');
      }
      
      // Update notification to downloading
      addToast(
        `Downloading ${reportName}...`,
        'info',
        0
      );
      
      // Download the file
      const blob = await reportingAPI.downloadReport(desiredFilename);
      
      // Trigger browser download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = desiredFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      // Show success notification
      addToast(
        `${reportName} downloaded successfully`,
        'success',
        3000
      );
      
      // Cleanup
      try {
        await reportingAPI.cleanupReport(desiredFilename);
      } catch (cleanupError) {
        console.warn('Could not cleanup file:', cleanupError);
      }
      
    } catch (err) {
      console.error('Error downloading report:', err);
      const errorMsg = `Failed to download ${reportName}: ${err.response?.data?.error || err.message}`;
      setError(errorMsg);
      addToast(errorMsg, 'error', 8000);
    } finally {
      // Remove from loading state
      if (isActivityItem) {
        setDownloadingActivity(prev => prev.filter(id => id !== reportId));
      } else {
        setDownloadingReports(prev => prev.filter(id => id !== reportId));
      }
    }
  };

  const handleViewReport = async (executionId) => {
    try {
      await reportingAPI.updateReportAnalytics(executionId, 'view');
      const activity = await reportingAPI.getRecentActivity();
      setRecentActivity(activity);
    } catch (err) {
      console.error('Error updating view count:', err);
    }
  };

  // Form submission handlers
  const handleCreateReport = async (e) => {
    e.preventDefault();
    
    // Get required values from localStorage
    const businessId = localStorage.getItem('selectedBusinessId');
    const userId = localStorage.getItem('user');
    
    // Validate required fields before submission
    if (!businessId) {
      addToast('Please select a business first', 'error');
      setError('No business selected. Please select a business from the dropdown.');
      return;
    }
    
    if (!userId) {
      addToast('User session expired. Please log in again.', 'error');
      setError('User session expired');
      return;
    }
    
    // Validate form data
    if (!createForm.form.name || createForm.form.name.trim() === '') {
      addToast('Report name is required', 'error');
      return;
    }
    
    // Filter out empty recipients
    const validRecipients = createForm.form.recipients.filter(email => email && email.trim() !== '');
    if (validRecipients.length === 0) {
      addToast('At least one recipient email is required', 'error');
      return;
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = validRecipients.filter(email => !emailRegex.test(email));
    if (invalidEmails.length > 0) {
      addToast(`Invalid email addresses: ${invalidEmails.join(', ')}`, 'error');
      return;
    }
    
    try {
      // Include timezone in the report data
      const reportData = {
        ...createForm.form,
        recipients: validRecipients,
        business_id: businessId,
        created_by: userId,
        timezone: createForm.form.timezone || userTimezone,
        date_range_config: {
          ...createForm.form.date_range_config,
          timezone: createForm.form.timezone || userTimezone
        }
      };
      
      console.log('Business ID for new report:', businessId);
      console.log('Creating report with timezone-aware configuration:', reportData);
      
      // Pass the complete reportData instead of using the form's submitForm
      await reportingAPI.createScheduledReport(reportData);
      
      // Reset form and show success
      createForm.resetForm();
      setShowCreateModal(false);
      loadDashboardData();
      addToast(`Report created successfully for timezone ${getTimezoneDisplayName(reportData.timezone)}`, 'success');
      
    } catch (err) {
      console.error('Create report error:', err);
      console.error('Error response:', err.response?.data);
      
      // Show specific error message from server
      const errorMessage = err.response?.data?.error || 'Failed to create report';
      const errorDetails = err.response?.data?.details || '';
      
      addToast(`${errorMessage}${errorDetails ? ': ' + errorDetails : ''}`, 'error');
      setError(errorMessage);
    }
  };

  const handleUpdateReport = async (e) => {
    e.preventDefault();
    
    // Validate form data
    if (!editForm.form.name || editForm.form.name.trim() === '') {
      addToast('Report name is required', 'error');
      return;
    }
    
    // Filter out empty recipients
    const validRecipients = editForm.form.recipients.filter(email => email && email.trim() !== '');
    if (validRecipients.length === 0) {
      addToast('At least one recipient email is required', 'error');
      return;
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = validRecipients.filter(email => !emailRegex.test(email));
    if (invalidEmails.length > 0) {
      addToast(`Invalid email addresses: ${invalidEmails.join(', ')}`, 'error');
      return;
    }
    
    try {
      // Include timezone in the update data
      const reportData = {
        ...editForm.form,
        recipients: validRecipients,
        timezone: editForm.form.timezone || userTimezone, // Ensure timezone is included
        date_range_config: {
          ...editForm.form.date_range_config,
          timezone: editForm.form.timezone || userTimezone
        }
      };
      
      console.log('Updating report with timezone configuration:', reportData);
      
      await editForm.submitForm((formData) => 
        reportingAPI.updateScheduledReport(selectedReport.id, reportData)
      );
      addToast(`Report updated successfully for timezone ${getTimezoneDisplayName(reportData.timezone)}`, 'success');
    } catch (err) {
      console.error('Update report error:', err);
      console.error('Error response:', err.response?.data);
      
      const errorMessage = err.response?.data?.error || 'Failed to update report';
      const errorDetails = err.response?.data?.details || '';
      
      addToast(`${errorMessage}${errorDetails ? ': ' + errorDetails : ''}`, 'error');
      setError(errorMessage);
    }
  };

  if (loading && scheduledReports.length === 0) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-wrapper">
          <LoadingContainer />
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className='high'>
        <Header />
      </div>
      
      <div className="dashboard-wrapper">
        <DashboardHeader
          onCreateReport={() => setShowCreateModal(true)}
          onRefresh={loadDashboardData}
          lastRefresh={lastRefresh}
          loading={loading}
          userTimezone={userTimezone}
        />

        <ErrorBanner
          error={error}
          onDismiss={() => setError(null)}
        />

        <div className="dashboard-content">
          <StatsGrid stats={dashboardStats} />

          <div className="main-content-grid">
            <ScheduledReportsSection
              reports={scheduledReports}
              onCreateReport={() => setShowCreateModal(true)}
              onToggleReport={handleToggleReport}
              onEditReport={handleEditReport}
              onDeleteReport={handleDeleteReport}
              onDownloadReport={(report) => handleDownloadReport(report, false)}
              formatSchedule={formatSchedule}
              loading={loading}
              downloadingReports={downloadingReports}
              userTimezone={userTimezone}
            />

            <RecentActivitySection
              activity={recentActivity}
              onViewReport={handleViewReport}
              onDownloadReport={(item, filename) => handleDownloadReport(item, true)}
              downloadingActivity={downloadingActivity}
            />
          </div>
        </div>
      </div>

      {/* Modals with timezone and period selection */}
      <CreateReportModal
        show={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateReport}
        reportForm={createForm.form}
        updateReportForm={createForm.updateForm}
        updateFrequencyConfig={createForm.updateFrequencyConfig}
        addRecipient={createForm.addRecipient}
        removeRecipient={createForm.removeRecipient}
        updateRecipient={createForm.updateRecipient}
        onFrequencyChange={(frequency) => handleFrequencyChange(frequency, 'create')}
        onPeriodChange={(period) => handlePeriodChange(period, 'create')}
        onTimezoneChange={(timezone) => handleTimezoneChange(timezone, 'create')}
        loading={createForm.loading}
        userTimezone={userTimezone}
        getTimezoneDisplayName={getTimezoneDisplayName}
      />

      <EditReportModal
        show={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedReport(null);
        }}
        onSubmit={handleUpdateReport}
        selectedReport={selectedReport}
        reportForm={editForm.form}
        updateReportForm={editForm.updateForm}
        updateFrequencyConfig={editForm.updateFrequencyConfig}
        addRecipient={editForm.addRecipient}
        removeRecipient={editForm.removeRecipient}
        updateRecipient={editForm.updateRecipient}
        onFrequencyChange={(frequency) => handleFrequencyChange(frequency, 'edit')}
        onPeriodChange={(period) => handlePeriodChange(period, 'edit')}
        onTimezoneChange={(timezone) => handleTimezoneChange(timezone, 'edit')}
        loading={editForm.loading}
        userTimezone={userTimezone}
        getTimezoneDisplayName={getTimezoneDisplayName}
      />

      <DeleteConfirmModal
        show={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setSelectedReport(null);
        }}
        onConfirm={confirmDeleteReport}
        selectedReport={selectedReport}
        formatSchedule={formatSchedule}
      />
    </div>
  );
};

export default ReportingDashboard;